import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import AppWall from "./AppWall";
import { shouldShowAppWall, webPlatform } from "./appPromo";
import { sendEvent } from "./engagement";
import { ErrorBoundary } from "./ErrorBoundary";
import { initNativeApp, isNativeApp } from "./native";
import { isStandalone } from "./pwaInstall";
import { initObservability } from "./observability";
import "./styles.css";
import "./wow.css";
import "./elevate.css";
import "./pro.css";
import "./v2now.css";
import "./v3.css";
import "./atelier.css";
import "./edition.css";
import "./retro.css";
// v4 layer loads last so it wins equal-specificity ties over every sheet above.
import "./v4.css";
// LUMEN: the white-gallery treatment of the light theme. Every rule is scoped
// to [data-theme="light"] so the other themes keep their souls.
import "./lumen.css";
// NOIR: the v4 identity. Cinema (dark) is the default voice of v4: the story's
// photograph paints the room, the Edition floats as a lit sheet over it. Loads
// last; rules are scoped to [data-theme="dark"] plus a few theme-neutral v4
// instruments (the filmstrip).
import "./noir.css";
// WEB GLASS: the site's material (lens edge, specular, depth, ambient tint) on top
// of the blur v4 already runs. Loads last so it wins equal-specificity ties.
import "./webglass.css";
// The simpler card (2026-09-13): one folio line, pointers, one disclosure. After every skin,
// before phoneread.css so the phone keeps its last word.
import "./simpler.css";
// The Kindle layer (2026-09-13): the reading face, the Aa sheet, the footer, Paper.
import "./kindle.css";
// PHONE WEB: the reader as it is laid out for a phone in a browser. Loads last of all, and
// every rule inside is scoped to html.phone-web, so it can only ever affect that one visitor.
import "./phoneread.css";
import { initGlassPointer } from "./glassPointer";
import { isPhoneWeb } from "./phone";

initObservability();
void initNativeApp();
// The specular follows the cursor. Attaches nothing on touch devices or under
// reduced motion; see glassPointer.ts for the cost controls.
initGlassPointer();

// SELF-HEAL A BROKEN OR STALE CHUNK.
//
// Cloudflare Pages serves the SPA fallback for any path it cannot find, and `_headers` stamps
// everything under /assets/* with `immutable, max-age=31536000`. So a request that lands during
// the seconds between a new index.html going live and its assets propagating gets HTML back at a
// .js URL, WITH a one-year immutable header on it, and that wrong answer is then pinned in the
// edge cache under the asset's own name. It happened to insnip.com on 2026-08-04: the page white
// screened with "Expected a JavaScript-or-Wasm module script but the server responded with a MIME
// type of text/html", while the same deployment served perfectly on its *.pages.dev URL, because
// only one Accept-Encoding variant of the cache key had been poisoned.
//
// Vite fires `vite:preloadError` when a lazily-imported chunk fails to load, which is exactly this
// case. Reload once, with a cache-busting query so the document itself is re-fetched fresh; the
// sessionStorage latch means a genuinely offline reader gets the error boundary rather than a
// reload loop.
//
// IT HAPPENED AGAIN ON 2026-08-05, AND THIS HANDLER COULD NOT HELP, because that time the
// poisoned asset was the ENTRY module rather than a lazy chunk. This file is inside it, so none
// of the code below ever ran and the reader got a bare shell reading "Skip to content". The
// companion guard for that case lives in public/boot-guard.js, which is served with the ordinary
// rule rather than the immutable one and therefore still runs when this bundle will not parse.
// Keep the two in step: this one recovers a chunk, that one recovers the boot.
//
// AND AGAIN ON 2026-09-08, the third time. What the first two occurrences hid: reloading the
// DOCUMENT never fixed anything. index.html is served no-cache, so it was never the stale half.
// The reload came back and asked for the same hashed URL, whose poisoned copy is held under
// `immutable, max-age=31536000`, so the retry failed identically and the latch gave up. The
// asset has to be repaired before a reload is worth spending: `cache: "reload"` sends the
// request with no-cache, which makes the edge revalidate instead of answering from its poisoned
// copy, and re-stores the correct JavaScript under that key. Best-effort by construction, since
// either outcome still reloads and an offline reader behaves exactly as before.
window.addEventListener("vite:preloadError", (event) => {
  const KEY = "insnip.chunkReload";
  try {
    if (sessionStorage.getItem(KEY)) return; // already tried once this session: let it surface
    sessionStorage.setItem(KEY, "1");
  } catch {
    return; // no sessionStorage (private mode): never risk a loop
  }
  event.preventDefault();
  const reload = () => {
    const url = new URL(window.location.href);
    url.searchParams.set("_r", String(Date.now()).slice(-6));
    window.location.replace(url.toString());
  };
  // Vite names the chunk it could not preload on the event payload; the message carries the URL
  // when the property does not. Repair that exact key, then reload.
  const payload = (event as Event & { payload?: { message?: string } }).payload;
  const found = /https?:\/\/\S+?\.js/.exec(payload?.message ?? "")?.[0];
  if (found && typeof fetch === "function") {
    fetch(found, { cache: "reload", credentials: "same-origin" }).then(reload, reload);
  } else {
    reload();
  }
});

// Stamp the saved theme BEFORE first paint. App.tsx applies it in an effect, which is
// after paint: without this, the bare :root fallback palette (indigo/purple) flashes
// and can be captured as "the" chrome color. Same key + JSON shape as App.tsx.
//
// v4 NOIR migration: Cinema (dark) is v4's default voice. Returning visitors carry an
// insnip.theme written by an older build (often set while exploring the theme menu),
// which would hide the redesign behind their stale choice, so the FIRST load of this
// build lands everyone on Cinema once (guarded by insnip.themeV4). The theme picker
// still works and every later choice sticks.
//
// Midnight (oled) and Auto were retired from the picker (see THEME_OPTIONS in App.tsx): Auto
// only ever resolved to Cinema or Daylight, and Midnight was a shade of Cinema. Anyone holding
// one is moved to Cinema HERE, pre-paint, and the stored value is rewritten, so they are never
// left wearing a theme the menu can no longer give back to them.
try {
  const KNOWN = ["dark", "light", "sepia", "retro"];
  const RETIRED = ["oled", "auto"];
  let theme = "dark";
  if (localStorage.getItem("insnip.themeV4") === "1") {
    const stored = localStorage.getItem("insnip.theme");
    const parsed = stored ? JSON.parse(stored) : "dark";
    if (RETIRED.includes(parsed)) {
      theme = "dark";
      localStorage.setItem("insnip.theme", JSON.stringify("dark"));
    } else {
      theme = KNOWN.includes(parsed) ? parsed : "dark";
    }
  } else {
    localStorage.setItem("insnip.themeV4", "1");
    localStorage.setItem("insnip.theme", JSON.stringify("dark"));
  }
  document.documentElement.setAttribute("data-theme", theme);
} catch {
  document.documentElement.setAttribute("data-theme", "dark");
}

// THE PHONE WALL, DECIDED BEFORE THE READER MOUNTS.
//
// Not an early return inside App(): that component runs some three thousand lines of hooks
// before its first return, so a conditional return there would break the rules of hooks, and
// even placed after them it would still have fetched the feed, restored the session and started
// the round for a reader who is never shown any of it. Deciding at the mount point means a
// walled visitor costs one user-agent test and zero requests.
//
// Everything about WHO is walled lives in appPromo.shouldShowAppWall, next to the rules it
// shares with the server gate in functions/_device.js.
const wallPlatform = webPlatform();
const showWall = shouldShowAppWall({ nativeApp: isNativeApp, standalone: isStandalone() });

// PHONE WEB, STAMPED BEFORE FIRST PAINT.
//
// html, body and #root are ANCESTORS of the app shell, so a descendant selector inside the
// React tree can never reach them, and a class applied in an effect lands after the first
// frame. Both problems are solved by stamping the root element here, next to the theme, which
// is the same technique the native shell uses for its own ground colour.
//
// The class is the ONLY switch for phoneread.css, so every rule in that sheet is inert on a
// desktop, a tablet, the native app, an installed PWA, a crawler, and ?desktop=1.
try {
  if (isPhoneWeb()) document.documentElement.classList.add("phone-web");
} catch {
  /* a class name can never be worth a boot failure */
}

createRoot(document.getElementById("root") as HTMLElement).render(
  <StrictMode>
    <ErrorBoundary>
      {showWall ? (
        <AppWall
          platform={wallPlatform}
          onSeen={() => sendEvent({ type: "app_wall_seen", tag: wallPlatform })}
          onStoreClick={(surface) => sendEvent({ type: "app_get_click", tag: surface })}
        />
      ) : (
        <App />
      )}
    </ErrorBoundary>
  </StrictMode>
);

// The boot succeeded, so release the entry-module guard's one-shot latch (see index.html).
// Clearing it HERE rather than only in that guard's own timer means the next genuinely broken
// boot gets its retry too: a reader whose session latched during an outage would otherwise
// carry the latch for the rest of the session and never self-heal a second time.
try {
  sessionStorage.removeItem("insnip.bootReload");
} catch {
  /* no sessionStorage (private mode): the guard never latched, so there is nothing to clear */
}

// Register the service worker for offline support + installability (best-effort).
if ("serviceWorker" in navigator) {
  // When a NEW service worker version is deployed it skipWaiting()s and claims clients,
  // firing `controllerchange`. Reload once so the user immediately gets the fresh app
  // shell instead of being stuck on a stale cached bundle (e.g. an old build whose API
  // URL wasn't wired up, which silently falls back to the bundled cards.json). Guarded so
  // it only fires on a real UPDATE (page already had a controller), never the first
  // install, and only once (no reload loop).
  if (navigator.serviceWorker.controller) {
    let reloading = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (reloading) return;
      reloading = true;
      window.location.reload();
    });
  }
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {});
  });
  // A tapped web push (public/firebase-messaging-sw.js) hands this tab the story's URL by
  // message rather than navigating it: that worker is scoped to /firebase-push/ and controls
  // no page. Only a URL on this origin is followed, checked here as well as in the worker.
  navigator.serviceWorker.addEventListener("message", (event) => {
    try {
      const data = (event as MessageEvent).data as { type?: unknown; url?: unknown } | null;
      if (!data || data.type !== "insnip:notificationclick" || typeof data.url !== "string") return;
      const target = new URL(data.url, window.location.origin);
      if (target.origin === window.location.origin) window.location.href = target.href;
    } catch {
      /* a malformed message is ignored */
    }
  });
}
